// PostHog client wrapper for the gerencial dashboard (ADR-0101, pending).
//
// MVP cut (2026-08-25, explicit founder decision): replay is on
// app-wide with no route exclusions and no hardened masking config —
// PostHog's own defaults apply. That is a conscious, informed
// risk-acceptance to ship a functional integration fast; the safety
// work (excluding /admin/**, deny-by-default masking, the compliance
// package) is tracked as follow-up tickets, not implemented here.
// See pm/tickets.json (#0537–#0540).
//
// Until 2026-09-09 none of this ever ran in a deployed tier: the CSP in
// public/_headers (widened to /* on 2026-08-08, before this file existed)
// had no PostHog origin, so init's /flags call, every capture and the
// replay recorder were all silently blocked. The risk accepted above
// therefore materializes only from the deploy that carries the CSP fix.
//
// The replay recorder (rrweb) is pre-bundled via
// posthog-js/dist/all-external-dependencies and
// disable_external_dependency_loading, so script-src can stay 'self':
// this app keeps the admin refresh token in localStorage
// (src/lib/api/session.ts) and posthog-js has no SRI hook, so letting the
// PostHog CDN into script-src would make a CDN compromise an admin-token
// theft. Cost: the recorder ships in the lazy telemetry chunk (~365 KB
// raw, desktop-only app) and is pinned to the lockfile version.
//
// Module-singleton, not a Context Provider — matches this codebase's
// convention for cross-cutting external state (sessionStore.ts,
// bases.ts) rather than tying init to React's render/commit phase.
import type { PostHog } from "posthog-js";
import { env } from "../env";

let ph: PostHog | null = null;
let initialized = false;

type PendingIdentity = {
  userId: string;
  organizationId: string;
  email: string | null | undefined;
};

// Identity handed to identifyTelemetry() before the SDK finished
// loading (sessionStore's import-time boot-restore runs before
// main.tsx's initTelemetry() has even been called). Latest wins;
// drained once `ph` exists.
let pendingIdentity: PendingIdentity | null = null;

// The only place the email is read. It stays local to this predicate
// and never reaches PostHog (identify/group/capture/$set all take the
// opaque user id only).
export function isRecordingExcluded(email: string | null | undefined): boolean {
  const local = (email ?? "").split("@")[0] ?? "";
  return /^fmiqueldev/i.test(local);
}

function applyPendingIdentity(): void {
  if (!ph || !pendingIdentity) return;
  const { userId, organizationId, email } = pendingIdentity;
  ph.identify(userId);
  ph.group("organization", organizationId);
  if (isRecordingExcluded(email)) {
    ph.stopSessionRecording();
  } else {
    // Bare call on purpose — `startSessionRecording(true)` persists a
    // `$replay_override_*` flag in localStorage + cookie.
    ph.startSessionRecording();
  }
}

// Diagnostic-only: detects when PostHog's own host is CSP-blocked (a
// misconfigured `public/_headers` would silently no-op capture, which
// otherwise looks identical to "telemetry is just off"). Does not touch
// CSP or any PostHog init option — see CLAUDE.md, `_headers` is out of
// scope here. Logs at most once per distinct (directive, blocked-origin)
// pair for the page's lifetime, not once total and not unbounded.
const reportedCsp = new Set<string>();
let cspListenerInstalled = false;

function isPosthogUri(uri: string): boolean {
  if (!env.posthogHost) return false;
  return uri.startsWith(env.posthogHost) || /\.posthog\.com(\/|$)/.test(uri);
}

function installCspDiagnostic(): void {
  if (cspListenerInstalled) return;
  cspListenerInstalled = true;
  window.addEventListener("securitypolicyviolation", (ev) => {
    if (!isPosthogUri(ev.blockedURI)) return;
    let origin: string;
    try {
      origin = new URL(ev.blockedURI).origin;
    } catch {
      origin = ev.blockedURI;
    }
    const key = `${ev.effectiveDirective}|${origin}`;
    if (reportedCsp.has(key)) return;
    reportedCsp.add(key);
    console.warn(
      `[telemetry] CSP bloqueó ${ev.effectiveDirective} → ${ev.blockedURI}; revisa public/_headers`
    );
  });
}

export function initTelemetry(): void {
  if (initialized || !env.posthogKey) return;
  initialized = true;
  installCspDiagnostic();
  // Fire-and-forget by design — telemetry must never block or crash the
  // app; a failed dynamic import just leaves `ph` null (capture no-ops).
  // The extensions bundle registers window.__PosthogExtensions__ (rrweb,
  // the recorder, …) as a side effect and has to be evaluated before
  // init, which is what Promise.all + the ordering below guarantee.
  void Promise.all([import("posthog-js"), import("posthog-js/dist/all-external-dependencies")])
    .then(([{ default: posthog }]) => {
      posthog.init(env.posthogKey as string, {
        api_host: env.posthogHost,
        person_profiles: "identified_only",
        // Never <script>-inject recorder.js & co. from the PostHog CDN —
        // see the header comment. With the extensions pre-registered
        // above, this only closes the external path.
        disable_external_dependency_loading: true,
        // Recording starts only from applyPendingIdentity(): the founder
        // exclusion has to be decided before rrweb mounts. Default-on-
        // then-stop would record the pre-identify window.
        disable_session_recording: true,
      });
      ph = posthog;
      applyPendingIdentity();
    })
    .catch(() => {
      // Swallow — see above.
    });
}

export function capture(event: string, properties?: Record<string, unknown>): void {
  ph?.capture(event, properties);
}

// Called from the sessionStore's setSession() choke point (every login
// writes through it) and from its import-time boot-restore. The email
// is passed only so isRecordingExcluded() can gate the recorder; it
// never reaches identify(), group(), capture() or any `$set`. rut /
// displayName stay out entirely. No dedupe guard here: only those two
// call sites exist and token refresh does not funnel through
// setSession on the dashboard today.
export function identifyTelemetry(
  userId: string,
  organizationId: string,
  email: string | null | undefined
): void {
  pendingIdentity = { userId, organizationId, email };
  applyPendingIdentity();
}

export function resetTelemetry(): void {
  // Clear the slot first: a late init after logout must not identify a
  // device that no longer has a session.
  pendingIdentity = null;
  if (!ph) return;
  ph.stopSessionRecording();
  ph.reset();
}

export { ph as posthog };
